import {
  DriverField,
  GoodsType,
  OpenState,
  VehicleField,
} from '@/config/constants';

import { Config } from '@/config/types';
import { awsConfig } from '@/config/aws';

export const defaultConfig: Config = {
  env: import.meta.env.MODE || 'development',
  app: {
    logLevel: import.meta.env.VITE_LOG_LEVEL || 'info',
    defaultCountryCode: 'GB',
    fallbackTimezone: 'Europe/London',
    fallbackCurrencyCode: 'GBP',
    defaultLocale: 'en-GB',
    appDetailsRefreshMins: 30,
    maxContactDropDown: 10,
    useGoogleSignIn: false,
    useAppleSignIn: false,
    showPrices: true,
    gtmId: import.meta.env.VITE_GTM_ID,
    companyDetails: {},
    calendar: {
      pickupDurationMins: 15,
    },
    addressSearch: {
      searchResultCount: 5,
      gps: {
        radiusSearch: 50,
        retryRadius: 200,
        timeout: 3000,
        maximumAge: 300000,
      },
    },
    addressBook: {
      maxShown: 10,
      initialTypes: ['contact', 'recent', 'account'],
    },
    tracking: {
      refreshSecs: 60,
      display: {
        default: {
          driver: [
            DriverField.NAME,
            DriverField.PHOTO,
            DriverField.MOBILE,
            DriverField.LICENCE,
          ],
          vehicle: [VehicleField.DESC, VehicleField.REG],
        },
      },
    },
    googlePlaces: {
      apiKey: import.meta.env.VITE_GOOGLE_PLACES_KEY || '',
    },
    hereMaps: {
      apiKey: import.meta.env.VITE_HERE_MAPS_KEY || '',
      animateFocus: false,
      defaultLocation: { lat: 51.5347, lng: 0.1246 },
      fullRouteGoodsTypes: [GoodsType.PARCEL, GoodsType.PASSENGER],
      lines: {
        route: { style: { lineWidth: 5 } },
        tracking: {
          style: { strokeColor: '#e23645', lineWidth: 5, lineDash: [1, 2] },
          trackingTrailLength: 5,
        },
      },
      icons: {
        start: {
          bitmap:
            '<svg viewBox="0 0 80 127" xmlns="http://www.w3.org/2000/svg"><path d="M40 0C62 0 80 18 80 40C80 71 40 127 40 127C40 127 0 71 0 40C0 18 18 0 40 0Z" fill="#58D68D"/><circle cx="40" cy="40" r="15" fill="#2C2C2C"/></svg>',
          options: { size: { w: 25, h: 32 } },
        },
        via: {
          bitmap:
            '<svg viewBox="0 0 80 127" xmlns="http://www.w3.org/2000/svg"><path d="M40 0C62 0 80 18 80 40C80 71 40 127 40 127C40 127 0 71 0 40C0 18 18 0 40 0Z" fill="#F4D03F"/><circle cx="40" cy="40" r="15" fill="#2C2C2C"/></svg>',
          options: { size: { w: 25, h: 32 } },
        },
        end: {
          bitmap:
            '<svg viewBox="0 0 80 127" xmlns="http://www.w3.org/2000/svg"><path d="M40 0C62 0 80 18 80 40C80 71 40 127 40 127C40 127 0 71 0 40C0 18 18 0 40 0Z" fill="#D94C4C"/><circle cx="40" cy="40" r="15" fill="#2C2C2C"/></svg>',
          options: { size: { w: 25, h: 32 } },
        },
        location: {
          bitmap:
            '<svg viewBox="0 0 80 127" xmlns="http://www.w3.org/2000/svg"><path d="M40 0C62 0 80 18 80 40C80 71 40 127 40 127C40 127 0 71 0 40C0 18 18 0 40 0Z" fill="#2C2C2C"/><circle cx="40" cy="40" r="15" fill="#ffffff"/></svg>',
          darkBitmap:
            '<svg viewBox="0 0 80 127" xmlns="http://www.w3.org/2000/svg"><path d="M40 0C62 0 80 18 80 40C80 71 40 127 40 127C40 127 0 71 0 40C0 18 18 0 40 0Z" fill="#ffffff"/><circle cx="40" cy="40" r="15" fill="#2C2C2C"/></svg>',
          options: { size: { w: 25, h: 32 } },
        },
        tracking: {
          bitmap:
            '<svg viewBox="-41.2325 10.6538 588.3017 588.3017"><path fill="#2C2C2C" d="M 458.521 140.887 C 463.421 128.987 460.621 115.287 451.521 106.187 C 442.421 97.087 428.721 94.287 416.821 99.187 L 64.82 243.187 C 50.62 248.987 42.62 263.987 45.52 278.987 C 48.42 293.987 61.62 304.787 76.92 304.787 L 252.92 304.787 L 252.92 480.787 C 252.92 496.087 263.72 509.187 278.72 512.187 C 293.72 515.187 308.72 507.087 314.52 492.887 L 458.521 140.887 Z" style="transform-origin: 252.918px 304.805px;" transform="matrix(0.707107007504, -0.707107007504, 0.707107007504, 0.707107007504, 4.06849e-7, -0.000016299803)"></path></svg>',
          darkBitmap:
            '<svg viewBox="-41.2325 10.6538 588.3017 588.3017"><path fill="#ffffff" d="M 458.521 140.887 C 463.421 128.987 460.621 115.287 451.521 106.187 C 442.421 97.087 428.721 94.287 416.821 99.187 L 64.82 243.187 C 50.62 248.987 42.62 263.987 45.52 278.987 C 48.42 293.987 61.62 304.787 76.92 304.787 L 252.92 304.787 L 252.92 480.787 C 252.92 496.087 263.72 509.187 278.72 512.187 C 293.72 515.187 308.72 507.087 314.52 492.887 L 458.521 140.887 Z" style="transform-origin: 252.918px 304.805px;" transform="matrix(0.707107007504, -0.707107007504, 0.707107007504, 0.707107007504, 4.06849e-7, -0.000016299803)"></path></svg>',
          options: {
            anchor: {
              x: 15,
              y: 15,
            },
            size: {
              w: 30,
              h: 30,
            },
          },
          rotateIcon: true,
        },
      },
    },
    bookingForm: {
      prebookBufferMins: 1,
      arrivalPickupBufferMins: 10,
      fallbackDefaultDelayMins: 30,
      showFavouriteJourneys: true,
      showRecentJourneys: true,
      autoFavAddressSearch: true,
      sectionOpenStateDefaults: {
        when: OpenState.DEFAULT,
        jobFlags: OpenState.DEFAULT,
        where: OpenState.DEFAULT,
        goods: OpenState.DEFAULT,
        payment: OpenState.DEFAULT,
        tariffSlideshow: OpenState.ALWAYS,
        references: OpenState.DEFAULT,
        contact: OpenState.DEFAULT,
        jobExtras: OpenState.DEFAULT,
      },
      showTelOnViewOnlyTariff: true,
      displayMaxGoodsAsIcons: true,
      volumetricWeight: { divisor: 5000, unit: 'kg' },
      bookingConfirmationCloseSeconds: 5,
    },
    webxEmbed: {
      useSecondaryHeaderColour: false,
    },
    reporting: {
      defaultRows: 50,
      rowsPerPageOptions: [10, 50, 100, 500],
    },
  } as Config['app'], // companyDetails required per-brand, cast narrows this
  devLogControl: { cloudwatch: false },
  services: {
    credentials: {
      clientId: import.meta.env.VITE_SERVICE_CLIENT_ID || '',
      clientSecret: import.meta.env.VITE_SERVICE_CLIENT_SECRET || '',
    },
    authProxyUrl: import.meta.env.VITE_AUTH_PROXY_URL || '',
    loggerProxyUrl: import.meta.env.VITE_LOGGER_URL || '',
    trackingUrl: import.meta.env.VITE_TRACKING_URL || '',
    fdmBrowserUrl: import.meta.env.VITE_FDM_BROWSER_URL || '',
    functionsUrl: import.meta.env.VITE_FUNCTIONS_URL || '',
    websocketHost: import.meta.env.VITE_WEBSOCKET_HOST || '',
    adflexBaseUrl: JSON.parse(import.meta.env.VITE_ADFLEX_BASE_URL || '[]'),
    adflexScript: import.meta.env.VITE_ADFLEX_SCRIPT || '',
    apiMsTimeout: parseInt(import.meta.env.VITE_API_TIMEOUT || '30000'),
    debounceMs: 500,
    aws: { awsConfig },
    cloudWatch: {
      region: 'eu-west-2',
      intervalSecs: 300,
      batchSize: 50,
    },
    sns: { region: 'eu-west-2' },
    integraLink: {
      region: 'eu-west-2',
      hosts: JSON.parse(import.meta.env.VITE_INTEGRA_LINK || '[]'),
    },
    freedomQl: {
      region: 'eu-west-2',
      hosts: JSON.parse(import.meta.env.VITE_FREEDOM_QL || '[]'),
      guestHost: import.meta.env.VITE_FREEDOM_QL_GUEST || '',
    },
  } as Config['services'],
};
